import React, { useState } from 'react';
import { useDebounce } from 'use-debounce';
import { Autocomplete, Input, MenuItem, styled } from '@mui/material';

import { Wrapper } from './Wrapper';
import { CloseButton } from './CloseButton';
import { CustomPopper } from './CustomPopper';
import { useApiQuery } from '../../client/useQueryApi';

const StyledWrapper = styled(Wrapper)`
  height: 48px;
  border-color: rgba(0, 0, 0, 0.35) !important;
  & .autocomplete {
    display: flex;
    height: 100%;
    align-items: center;
    width: 100%;
    overflow: hidden;
  }

  & .MuiAutocomplete-noOptions,
  & .MuiAutocomplete-loading,
  & .MuiAutocomplete-option {
    padding: 6px 16px;
  }
`;

const StyledInput = styled('input')`
  display: flex;
  border: 0px;
  background: transparent;

  padding: 0px 4px;
  outline: 0px;
  min-width: 0px;
  width: 100%;
  font-size: 15px;
  flex-shrink: 1;
`;

const StyledOption = styled('span')`
  white-space: nowrap;
  overflow: hidden;
`;

type Props = {
  onClose?: () => void;
  onAdd: (name: string) => void;
  className?: string;
  autoFocus?: boolean;
  existing?: string[];
  placeholder?: string;
};

export const TagInput: React.FC<Props> = ({
  onClose,
  onAdd,
  className,
  autoFocus,
  existing,
  placeholder,
}) => {
  const [value, setValue] = useState('');
  const [search] = useDebounce(value, 500);

  const tags = useApiQuery({
    url: '/v2/projects/tags',
    method: 'get',
    query: {
      search,
    },
    options: {
      cacheTime: 0,
      keepPreviousData: true,
    },
  });

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      onClose?.();
    }
  };

  const options = (tags.data?._embedded?.tags?.map(({ name }) => name) || [])
    .filter((value, index, self) => {
      return self.indexOf(value) === index;
    })
    .map((tag) => ({
      label: tag,
      value: tag,
      isAdd: false,
    }));

  return (
    <StyledWrapper role="input" className={className}>
      <span style={{ position: 'relative' }}>
        <Autocomplete
          className="autocomplete"
          loading={tags.isFetching}
          autoHighlight
          sx={{ paddingLeft: '24px' }}
          noOptionsText="Nothing found"
          PopperComponent={CustomPopper}
          options={options}
          filterOptions={(options) => {
            const filtered = options.filter((o) =>
              o.value.toLowerCase().startsWith(search.toLowerCase())
            );
            if (
              search !== '' &&
              !options.find((item) => item.value === search)
            ) {
              filtered.push({
                value: search,
                label: '',
                isAdd: true,
              });
            }
            return filtered.filter((o) => !existing?.includes(o.value));
          }}
          inputValue={value}
          onInputChange={(_, value) => {
            setValue(value);
          }}
          onChange={(_, newValue) => {
            if (typeof newValue !== 'string') {
              onAdd(newValue.value);
            }
          }}
          getOptionLabel={() => ''}
          isOptionEqualToValue={() => true}
          renderOption={(attrs, option) => {
            return (
              <MenuItem {...attrs}>
                <StyledOption data-cy="tag-autocomplete-option">
                  {option.isAdd ? `Add "${search}"` : option.label}
                </StyledOption>
              </MenuItem>
            );
          }}
          ListboxProps={{ style: { padding: 0 } }}
          renderInput={(params) => (
            <div className="autocomplete" ref={params.InputProps.ref}>
              <StyledInput
                {...params.inputProps}
                data-cy="tag-autocomplete-input"
                size={0}
                onKeyUp={handleKeyUp}
                autoFocus={autoFocus}
                placeholder={placeholder}
              />
              {onClose && <CloseButton onClick={onClose} />}
            </div>
          )}
        />
        <span style={{ position: 'absolute', paddingLeft: '4px', top: '0' }}>
          <svg
            width="19"
            height="20"
            viewBox="0 0 19 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.00403 0.500007C8.30231 0.501395 9.57464 0.863814 10.6788 1.54676C11.7829 2.2297 12.6754 3.20625 13.2564 4.36727C13.8374 5.52829 14.0841 6.82801 13.9689 8.12118C13.8537 9.41434 13.3811 10.65 12.604 11.69L18.714 17.79C18.8078 17.883 18.8822 17.9936 18.9329 18.1154C18.9837 18.2373 19.0098 18.368 19.0098 18.5C19.0098 18.632 18.9837 18.7627 18.9329 18.8846C18.8822 19.0064 18.8078 19.117 18.714 19.21C18.6211 19.3037 18.5105 19.3781 18.3886 19.4289C18.2667 19.4797 18.136 19.5058 18.004 19.5058C17.872 19.5058 17.7413 19.4797 17.6195 19.4289C17.4976 19.3781 17.387 19.3037 17.294 19.21L11.194 13.1C10.3064 13.7658 9.27314 14.2109 8.17959 14.3989C7.08603 14.5868 5.96347 14.5121 4.90447 14.1808C3.84548 13.8496 2.88039 13.2714 2.0888 12.4939C1.29721 11.7163 0.701805 10.7617 0.351675 9.70885C0.00154495 8.65595 -0.0932713 7.53491 0.0750427 6.43816C0.243357 5.34142 0.669979 4.3004 1.31973 3.40095C1.96948 2.5015 2.82374 1.7694 3.81206 1.26502C4.80038 0.760635 5.89444 0.498424 7.00403 0.500007ZM7.00403 12.5C7.99294 12.5 8.95963 12.2068 9.78188 11.6574C10.6041 11.1079 11.245 10.3271 11.6234 9.41342C12.0019 8.49979 12.1009 7.49446 11.908 6.52456C11.715 5.55465 11.2388 4.66374 10.5396 3.96447C9.8403 3.26521 8.94938 2.78901 7.97948 2.59608C7.00957 2.40315 6.00424 2.50217 5.09061 2.88061C4.17698 3.25905 3.39609 3.89991 2.84668 4.72216C2.29727 5.5444 2.00403 6.5111 2.00403 7.50001C2.00403 8.15662 2.13336 8.8068 2.38463 9.41342C2.6359 10.0201 3.0042 10.5712 3.46849 11.0355C3.93279 11.4998 4.48398 11.8681 5.09061 12.1194C5.69724 12.3707 6.34742 12.5 7.00403 12.5Z"
              fill="#B5B5B5"
            />
          </svg>
        </span>
      </span>
    </StyledWrapper>
  );
};
